<script setup>
import { computed } from 'vue';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useExactTimestamp } from 'shared/composables/useExactTimestamp';

import MessageStatus from './MessageStatus.vue';
import Icon from 'next/icon/Icon.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMapGetter } from 'dashboard/composables/store';
import { getReadableInboxByType } from 'dashboard/helper/inbox';
import { getInboxSource } from 'dashboard/helper/dvInbox';
import { useMessageContext } from './provider.js';

import { MESSAGE_STATUS, MESSAGE_TYPES } from './constants';

const exactTimestamp = useExactTimestamp();

const {
  isAFacebookInbox,
  isALineChannel,
  isAPIInbox,
  isASmsInbox,
  isATelegramChannel,
  isATwilioChannel,
  isAWebWidgetInbox,
  isAWhatsAppChannel,
  isAnEmailChannel,
  isAnInstagramChannel,
  isATiktokChannel,
} = useInbox();

const {
  status,
  isPrivate,
  createdAt,
  sourceId,
  messageType,
  contentAttributes,
  inboxId,
} = useMessageContext();

const inboxGetter = useMapGetter('inboxes/getInbox');

const readableTime = computed(() =>
  messageTimestamp(createdAt.value, 'LLL d, h:mm a')
);

const messageInbox = computed(() => {
  if (!inboxId.value) {
    return {};
  }

  return inboxGetter.value(inboxId.value) || {};
});

const normalizeSource = source => {
  const value = String(source || '').trim();
  if (!value) {
    return '';
  }

  return value.replace(/^whatsapp:/i, '');
};

const channelName = computed(() => {
  const inbox = messageInbox.value;
  const explicitName = String(inbox?.name || '').trim();
  if (explicitName) {
    return explicitName;
  }

  const channelType = inbox?.channel_type || inbox?.channelType || '';
  const phoneNumber =
    inbox?.phone_number || inbox?.phoneNumber || inbox?.phone || '';
  const source = normalizeSource(
    getInboxSource(channelType, phoneNumber, {
      ...inbox,
      website_url: inbox?.website_url || inbox?.websiteUrl || '',
    })
  );
  const readableType = getReadableInboxByType(channelType, phoneNumber);
  const readableChannel =
    readableType === 'chat'
      ? 'Inbox'
      : readableType.charAt(0).toUpperCase() + readableType.slice(1);

  return source ? `${readableChannel} ${source}` : readableChannel;
});

const showInboxLabel = computed(
  () => !isPrivate.value && channelName.value && channelName.value !== 'Inbox'
);

const inboxTooltip = computed(() => `Inbox: ${channelName.value}`);
const exactTime = computed(() => exactTimestamp(createdAt.value));

const showStatusIndicator = computed(() => {
  if (isPrivate.value) return false;
  // Don't show status for failed messages, we already show error message
  if (status.value === MESSAGE_STATUS.FAILED) return false;
  // Don't show status for deleted messages
  if (contentAttributes.value?.deleted) return false;

  if (messageType.value === MESSAGE_TYPES.OUTGOING) return true;
  if (messageType.value === MESSAGE_TYPES.TEMPLATE) return true;

  return false;
});

const isSent = computed(() => {
  if (!showStatusIndicator.value) return false;

  // Messages will be marked as sent for the Email channel if they have a source ID.
  if (isAnEmailChannel.value) return !!sourceId.value;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isASmsInbox.value ||
    isATelegramChannel.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.SENT;
  }

  // API inbox messages use real sent/delivered/read status values from the external system.
  if (isAPIInbox.value) return status.value === MESSAGE_STATUS.SENT;

  // All messages will be mark as sent for the Line channel, as there is no source ID.
  if (isALineChannel.value) return true;

  return false;
});

const isDelivered = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isASmsInbox.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.DELIVERED;
  }
  // API inbox messages use real delivered status from the external system.
  if (isAPIInbox.value) return status.value === MESSAGE_STATUS.DELIVERED;
  // All messages marked as delivered for the web widget inbox once they are sent.
  if (isAWebWidgetInbox.value) {
    return status.value === MESSAGE_STATUS.SENT;
  }
  if (isALineChannel.value) {
    return status.value === MESSAGE_STATUS.DELIVERED;
  }

  return false;
});

const isRead = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.READ;
  }

  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return status.value === MESSAGE_STATUS.READ;
  }

  return false;
});

// DV: reactions stored by DvMessageReactionService (WhatsApp/Instagram, account 1).
const reactions = computed(() => {
  const attributes = contentAttributes.value || {};
  const list = attributes.dv_reactions || attributes.dvReactions || [];
  return Array.isArray(list) ? list.filter(reaction => reaction?.emoji) : [];
});

const reactionTooltip = reaction =>
  reaction.by === 'agent' ? 'Реакция менеджера' : 'Реакция клиента';

const statusToShow = computed(() => {
  if (isRead.value) return MESSAGE_STATUS.READ;
  if (isDelivered.value) return MESSAGE_STATUS.DELIVERED;
  if (isSent.value) return MESSAGE_STATUS.SENT;

  return MESSAGE_STATUS.PROGRESS;
});
</script>

<template>
  <div class="text-xs flex flex-wrap items-center gap-x-1.5 gap-y-1">
    <span
      v-for="reaction in reactions"
      :key="`${reaction.by}-${reaction.emoji}`"
      v-tooltip.top="reactionTooltip(reaction)"
      class="inline-flex items-center rounded-full bg-n-alpha-2 px-1.5 text-sm leading-5 ring-1 ring-n-strong/40"
      data-dv-reaction
    >
      {{ reaction.emoji }}
    </span>
    <div class="inline">
      <time
        v-tooltip.top="{
          content: exactTime,
          delay: { show: 500, hide: 0 },
        }"
        class="inline"
      >
        {{ readableTime }}
      </time>
    </div>
    <span
      v-if="showInboxLabel"
      v-tooltip.top="inboxTooltip"
      class="inline-flex max-w-[11rem] items-center truncate rounded-md border border-current/20 px-1.5 py-px text-[10px] font-medium leading-4 opacity-90"
    >
      {{ channelName }}
    </span>
    <Icon v-if="isPrivate" icon="i-lucide-lock-keyhole" class="size-3" />
    <MessageStatus v-if="showStatusIndicator" :status="statusToShow" />
  </div>
</template>
